import usePageSEO from '../hooks/usePageSEO';
import { WebPageSchema } from '../components/JsonLd';

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowUpRight } from 'lucide-react';
import Footer from '../components/Footer';
import blogPosts from '../data/blogPosts';

const BlogListPage = () => {
  usePageSEO({
    title: 'Read Our Blog | AI for Financial Operations | Plouton AI',
    description: 'Read our latest articles and tips on using AI for your finance operations. Learn how AI agents can speed up your daily bank reconciliation. Follow our blog now!',
    path: '/blog'
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20">
      <WebPageSchema
        name="Blog | Plouton AI"
        description="Insights on AI automation, financial close, and how modern finance teams eliminate manual work."
        url="https://plouton.co/blog"
      />
      {/* Hero */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/30 via-[#0a0a0f] to-[#0a0a0f]" />
          <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[150px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block">
              Blogs
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
              Insights for Modern<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-300">Finance Teams</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-xl">
              Deep dives on AI automation, financial close, and how the best finance teams are eliminating manual work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog List */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="border-t border-slate-800">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 py-8 border-b border-slate-800/70 hover:bg-slate-900/30 -mx-4 px-4 rounded-lg transition-colors"
                  data-testid={`blog-card-${post.slug}`}
                >
                  {/* Meta left */}
                  <div className="flex items-center gap-4 sm:w-48 sm:flex-shrink-0">
                    <span className="text-sm text-slate-500 font-mono">
                      {formatDate(post.publishDate)}
                    </span>
                  </div>

                  {/* Content center */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-600">
                        <Clock className="w-3 h-3" />
                        {post.readTime} min
                      </span>
                    </div>

                    <h2 className="font-heading text-lg sm:text-xl font-semibold text-white group-hover:text-orange-300 transition-colors leading-snug mb-1.5">
                      {post.title}
                    </h2>

                    <p className="text-slate-500 text-sm leading-relaxed line-clamp-1 sm:line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>

                  {/* Arrow right */}
                  <div className="hidden sm:flex items-center flex-shrink-0">
                    <div className="w-9 h-9 rounded-full border border-slate-800 flex items-center justify-center group-hover:border-orange-500/50 group-hover:bg-orange-500/10 transition-all">
                      <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-orange-400 transition-colors" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListPage;
